/**
 * Whether a Status message says anything about a lift being reachable.
 *
 * # WHY THIS IS NOT `=== Connected && active`
 *
 * `extractConnectionStatus` admits three enum members — `Connected`,
 * `Disconnected` and `Heartbeat` — and both live decode paths then collapsed
 * them with `variantMessage(...) === Status.Connected && active`. That makes
 * every member that is not `Connected` mean "offline", which is precisely
 * backwards for `Heartbeat`: the heartbeat is the vendor keepalive VTS writes
 * at `heartbeat_freq`, and it is an ALTERNATING bit, so half of its frames
 * arrive with `active: false`. Positive evidence of liveness was being read as
 * proof of death.
 *
 * The consequence measured on a real six-car replay: three `Heartbeat` frames
 * arrive about a second after the CDO loads and latch three lifts to
 * `connected: false`. `rollUpBank` skips a disconnected lift, and on that bank
 * exactly ONE lift emits unassigned hall calls — one of the three. The only
 * `Connected` message in the whole capture carries `lift_id == bank_id`, so it
 * routes to the bank and can never clear a lift's flag. There is no path back.
 *
 * Over 716.9 seconds a hall call was registered 97.8% of the time and the
 * dashboard lit a stripe for 3.5% of it: 143 of 147 state changes suppressed.
 * What an operator sees is hall calls working for about 25 seconds after every
 * reconnect and then going dark — which is reported as blinking, because the
 * reconnect re-arms it.
 *
 * There is a faster variant on any site whose CDO maps a per-lift `Connected`
 * bit: Connected → true, Heartbeat → false, Connected → true, at the heartbeat
 * frequency. That is genuine flicker at ~1Hz from these same two lines.
 *
 * # THE THREE-VALUED RETURN
 *
 * `null` means "this message is not about connectivity, leave the flag alone".
 * It is not `false`. Collapsing the two is the whole defect, so the type makes
 * the distinction unrepresentable-by-accident rather than relying on a comment.
 */
export type ConnectivityVerdict = boolean | null;

/** The enum members a connectivity verdict can be drawn from. */
export interface ConnectivityEnums {
  connected: number;
  disconnected: number;
}

/**
 * Decide connectivity from one Status member and its active bit.
 *
 * - `Connected`    — authoritative. `active` is the answer.
 * - `Disconnected` — offline, unchanged from the previous behaviour.
 * - anything else  — including `Heartbeat`: not a connectivity statement, so
 *   the caller must leave the existing flag untouched.
 *
 * TWO THINGS DELIBERATELY NOT CHANGED, because there is evidence for the
 * heartbeat defect and none for either of these:
 *
 * 1. `Disconnected` still reports offline regardless of its `active` bit.
 *    Inverting it — reading `Disconnected(active: false)` as "the disconnect
 *    condition is absent, therefore connected" — is defensible and is what a
 *    bitmask reading would suggest, but no capture shows a vendor using it that
 *    way, and getting it wrong would invent a reconnection that never happened.
 *    Two existing tests pin the current behaviour; they are left passing rather
 *    than rewritten to match a guess.
 * 2. `Heartbeat` is not treated as evidence of LIFE either. It would be
 *    defensible, but it would also mean a lift that stops heartbeating never
 *    goes offline without a separate timeout, and inventing a liveness timeout
 *    here would be a behaviour change hiding inside a decode fix.
 */
export function connectivityFromStatus(
  member: number | null | undefined,
  active: boolean,
  enums: ConnectivityEnums,
): ConnectivityVerdict {
  if (member === enums.connected) return active;
  if (member === enums.disconnected) return false;

  return null;
}
